import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { supabase } from "./supabase";

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// Register a single token getter that always reads the freshest session
// from the supabase client. The orval-generated React Query hooks call
// this on every request via `customFetch`.
setAuthTokenGetter(async () => {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
});

/**
 * Read a cached Supabase session from localStorage synchronously so we can
 * skip the loading spinner for returning users. Supabase persists the session
 * under keys matching `sb-*-auth-token`; we look for the first matching key
 * and parse the stored JSON. If anything fails we return null and fall back
 * to the normal async path.
 */
function getCachedSession(): Session | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith("sb-") || !key.endsWith("-auth-token")) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { access_token?: string; expires_at?: number };
      // Treat as valid only if the token hasn't expired yet.
      if (parsed.access_token && parsed.expires_at) {
        if (parsed.expires_at * 1000 > Date.now()) {
          return parsed as unknown as Session;
        }
      }
    }
  } catch {
    // localStorage may be blocked (e.g. private browsing with strict settings).
  }
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Initialise from the cache so logged-in users never see a spinner flash.
  const cached = getCachedSession();
  const [session, setSession] = useState<Session | null>(cached);
  const [loading, setLoading] = useState(cached === null);

  useEffect(() => {
    let cancelled = false;

    // Always do a full async validation so the token gets refreshed if needed.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      setLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  async function signIn(email: string, password: string) {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
  }

  async function signUp(email: string, password: string) {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  }

  async function signOut() {
    await supabase.auth.signOut();
  }

  const value: AuthContextValue = {
    session,
    user: session?.user ?? null,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
